import { create } from 'zustand';
import { getChatbotConfigSection, updateChatbotConfigSection } from '../utils/chatbotConfigAccess';
import { useMultilingualSettings } from './useMultilingualSettings';

// Define the type for the texts section in chatbot_configs
interface ChatbotTexts {
  title: string;
  subtitle: string;
  welcomeMessage: string;
  inputPlaceholder: string;
  errorMessage: string;
}

// Default texts to use if none are found in the config
const DEFAULT_TEXTS: ChatbotTexts = {
  title: 'My AI Assistant',
  subtitle: 'Intelligent AI Assistant',
  welcomeMessage: 'Hello! I am your personal AI assistant. How can I help you today?',
  inputPlaceholder: 'Ask your question...',
  errorMessage: 'Sorry, an error occurred or you have reached your message limit. Please try again or start a new conversation.'
};

interface CustomTextsStore {
  title: string;
  subtitle: string;
  welcomeMessage: string;
  inputPlaceholder: string;
  errorMessage: string;
  texts: ChatbotTexts;
  setTexts: (texts: Partial<ChatbotTexts>, chatbotPublicId?: string) => Promise<void>;
  initializeTexts: (chatbotPublicId?: string) => Promise<void>;
  loading: boolean;
  initialized: boolean;
}

export const useCustomTexts = create<CustomTextsStore>((set) => ({
  title: DEFAULT_TEXTS.title,
  subtitle: DEFAULT_TEXTS.subtitle,
  welcomeMessage: DEFAULT_TEXTS.welcomeMessage,
  inputPlaceholder: DEFAULT_TEXTS.inputPlaceholder,
  errorMessage: DEFAULT_TEXTS.errorMessage,
  texts: DEFAULT_TEXTS,
  loading: false,
  initialized: false,

  // Initialize texts from the centralized config
  initializeTexts: async (chatbotPublicId) => {
    set({ loading: true });
    try {
      // Get the multilingual settings
      const { settings: multilingualSettings } = useMultilingualSettings.getState();

      // Get the texts section from the centralized chatbot config
      const textsConfig = await getChatbotConfigSection<ChatbotTexts>('texts', chatbotPublicId);
      
      // If multilingual is enabled, use translations from the current language
      if (multilingualSettings.enabled && multilingualSettings.currentLanguage) {
        const currentLang = multilingualSettings.currentLanguage;
        // Vérifier que currentLang existe et que la traduction existe
        const translation = currentLang ? multilingualSettings.translations[currentLang] : undefined;
        
        // Vérifier explicitement que la traduction et ses textes existent
        if (translation && translation.texts) {
          const textsTranslation = translation.texts;
          
          set({
            title: textsTranslation.title,
            subtitle: textsTranslation.subtitle,
            welcomeMessage: textsTranslation.welcomeMessage,
            inputPlaceholder: textsTranslation.inputPlaceholder,
            errorMessage: textsTranslation.errorMessage || DEFAULT_TEXTS.errorMessage,
            texts: {
              ...textsTranslation,
              errorMessage: textsTranslation.errorMessage || DEFAULT_TEXTS.errorMessage
            },
            initialized: true,
            loading: false
          });
          return;
        }
      }
      
      // Use the fetched texts or defaults if no translations
      const finalTexts = textsConfig || DEFAULT_TEXTS;
      set({
        title: finalTexts.title,
        subtitle: finalTexts.subtitle,
        welcomeMessage: finalTexts.welcomeMessage,
        inputPlaceholder: finalTexts.inputPlaceholder,
        errorMessage: finalTexts.errorMessage || DEFAULT_TEXTS.errorMessage,
        texts: {
          ...finalTexts,
          errorMessage: finalTexts.errorMessage || DEFAULT_TEXTS.errorMessage
        },
        initialized: true,
        loading: false
      });
    } catch (err) {
      console.error('Error initializing texts:', err);
      set({ 
        ...DEFAULT_TEXTS,
        texts: DEFAULT_TEXTS,
        initialized: true,
        loading: false 
      });
    }
  },

  // Update texts via the centralized config
  setTexts: async (newTexts, chatbotPublicId) => {
    const { settings: multilingualSettings } = useMultilingualSettings.getState();
    
    set((state) => {
      const updatedTexts = { 
        title: newTexts.title ?? state.title, 
        subtitle: newTexts.subtitle ?? state.subtitle, 
        welcomeMessage: newTexts.welcomeMessage ?? state.welcomeMessage, 
        inputPlaceholder: newTexts.inputPlaceholder ?? state.inputPlaceholder,
        errorMessage: newTexts.errorMessage ?? state.errorMessage
      };
      
      // If multilingual is enabled, update translations instead of base texts
      if (multilingualSettings.enabled && multilingualSettings.currentLanguage && chatbotPublicId) {
        const currentLang = multilingualSettings.currentLanguage;
        if (currentLang) {
          const updatedSettings = {
            ...multilingualSettings,
            translations: {
              ...multilingualSettings.translations,
              [currentLang]: {
                ...(multilingualSettings.translations[currentLang] || {}),
                texts: updatedTexts
              }
            }
          };
          
          updateChatbotConfigSection('multilingual_settings', updatedSettings, chatbotPublicId)
            .catch(err => console.error('Error saving translations:', err));
        }
      } else if (chatbotPublicId) {
        // Save to the centralized config if not in multilingual mode
        updateChatbotConfigSection('texts', updatedTexts, chatbotPublicId)
          .catch(err => console.error('Error saving texts:', err));
      }
      
      return { 
        ...state,
        ...updatedTexts,
        texts: updatedTexts
      };
    });
  }
}));

// Subscribe to multilingual settings changes
useMultilingualSettings.subscribe((state, prevState) => {
  try {
    // Vérifier les changements qui affectent les textes
    const enabledChanged = state.settings.enabled !== prevState.settings.enabled;
    const currentLanguage = state.settings.currentLanguage;
    const prevLanguage = prevState.settings.currentLanguage;
    const languageChanged = currentLanguage !== prevLanguage;
    
    if (enabledChanged || languageChanged) {
      console.log('Multilingual settings changed:', {
        enabledChanged,
        languageChanged,
        enabled: state.settings.enabled,
        currentLanguage,
        prevLanguage
      });
      
      if (state.settings.enabled && currentLanguage) {
        // Récupérer les traductions pour la langue actuelle
        const translation = state.settings.translations[currentLanguage];
        
        if (translation && translation.texts) {
          console.log('Applying translations for:', currentLanguage);
          const textsTranslation = translation.texts;
          
          useCustomTexts.setState({
            title: textsTranslation.title,
            subtitle: textsTranslation.subtitle,
            welcomeMessage: textsTranslation.welcomeMessage,
            inputPlaceholder: textsTranslation.inputPlaceholder,
            errorMessage: textsTranslation.errorMessage || DEFAULT_TEXTS.errorMessage,
            texts: {
              ...textsTranslation,
              errorMessage: textsTranslation.errorMessage || DEFAULT_TEXTS.errorMessage
            }
          });
        } else {
          console.log('No translations found for:', currentLanguage);
          // No translations available for this language, use defaults
          useCustomTexts.setState({
            ...DEFAULT_TEXTS,
            texts: DEFAULT_TEXTS
          });
        }
      } else if (!state.settings.enabled) {
        console.log('Multilingual disabled, resetting to defaults');
        // Mode multilingue désactivé, revenir aux textes par défaut
        useCustomTexts.setState({
          ...DEFAULT_TEXTS,
          texts: DEFAULT_TEXTS
        });
      }
    }
  } catch (error) {
    // Log any errors that might occur during translation processing
    console.error('Error processing multilingual settings change:', error);
  }
});